import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import * as React from 'react';

import type { VariantProps } from 'class-variance-authority';

import { Spinner } from './spinner';
import { cn } from './lib/utils';

const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-medium disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 ',
        outline:
          'border bg-card hover:bg-muted dark:hover:bg-muted/50 hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground ',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2 has-[>svg]:px-0',
        sm: 'h-9 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-11 rounded-md px-6 has-[>svg]:px-4',
        xl: 'h-10 rounded-md px-4 has-[>svg]:px-3 md:h-13 md:px-10 md:has-[>svg]:px-6',
        icon: 'size-9',
        'icon-sm': 'size-8 rounded-sm',
        'icon-lg': 'size-10',
      },
      active: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        outline: 'bg-primary text-primary-foreground hover:bg-primary/90',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant,
  size,
  isActive = false,
  isLoading = false,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    isLoading?: boolean;
    isActive?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  // While loading, keep the real children in the DOM but invisible so the button
  // holds its natural width (no shrink-to-spinner layout shift), and overlay the
  // spinner centered on top. Nesting the children in a span also stops the bare
  // spinner from tripping `has-[>svg]:px-0` (which would zero the padding).
  // asChild can't take a fragment (Slot needs a single child), so it keeps the
  // simple swap — isLoading + asChild is not a real combination in practice.
  const content =
    isLoading && !asChild ? (
      <>
        {/* gap-[inherit] so the placeholder reproduces the button's own resolved
            gap (base gap-2, or a gap-1.5/gap-3 override) — a hardcoded gap would
            mis-size the idle width and reintroduce a small layout shift. */}
        <span className="invisible inline-flex items-center gap-[inherit]">
          {props.children}
        </span>
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner className="size-5" />
        </span>
      </>
    ) : isLoading ? (
      <Spinner className="size-5" />
    ) : (
      props.children
    );

  return (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({
          variant,
          size,
          active: isActive ? variant : undefined,
          className,
        }),
        isLoading && !asChild && 'relative',
      )}
      {...props}
    >
      {content}
    </Comp>
  );
}

export { Button, buttonVariants };
